import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'
import { FormField } from '@/components/ui/FormField'
import { LoadingScreen } from '@/components/ui/LoadingScreen'
import {
  savePendingAdminInviteToken,
  readPendingAdminInviteToken,
  clearPendingAdminInviteToken,
} from '@/lib/pendingAdminInvite'

// Mirrors AcceptInstructorInvite.tsx's token-capture mechanism exactly (URL fragment,
// never a query param; held in localStorage, not sessionStorage — see
// pendingAdminInvite.ts — so it survives a sign-up/confirm-email/sign-in round trip,
// including the confirmation link opening in a brand new tab). Read by AuthCallback.tsx
// (to route the post-confirmation button to /admin/login instead of /login) and
// AdminLogin.tsx (to redirect here after sign-in instead of the ordinary /app/admin
// destination).

const ERROR_MESSAGES: Record<string, string> = {
  not_found:        'This invite link is invalid or has already been used.',
  missing_token:     'This invite link is invalid.',
  expired:           'This invitation has expired. Ask a superadmin to send a new one.',
  revoked:           'This invitation has been revoked.',
  already_used:      'This invitation was already accepted by a different account.',
  email_mismatch:    'This invitation was sent to a different email address. Sign in with the account that received it.',
  already_has_role:  'This account already has a role on DriveTrack and cannot become an admin.',
  unauthorized:      'Please sign in to accept this invitation.',
  internal_error:    'Something went wrong. Please try again.',
}

// email_mismatch is retryable simply by signing out and signing back in as the invited
// email — the token must survive the failed attempt rather than being discarded as if the
// invitation itself were spent.
const RETRYABLE_BY_SWITCHING_ACCOUNT = new Set(['email_mismatch'])

type Outcome =
  | { kind: 'pending' }
  | { kind: 'success' }
  | { kind: 'error'; message: string; code?: string }
  | { kind: 'invalid_link' }

function mapSignUpError(msg: string): string {
  if (msg.includes('already registered') || msg.includes('User already registered'))
    return 'An account with this email already exists. Sign in instead.'
  if (msg.includes('Password should be at least'))
    return 'Password must be at least 10 characters.'
  return 'Could not create account. Please try again.'
}

export default function AcceptAdminInvite() {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  const [token, setToken] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'pending' })
  const attempted = useRef(false)

  const [creatingAccount, setCreatingAccount] = useState(false)
  const [signupEmail,    setSignupEmail]    = useState('')
  const [signupPassword, setSignupPassword] = useState('')
  const [signupError,    setSignupError]    = useState('')
  const [checkEmailSent, setCheckEmailSent] = useState(false)

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const fromUrl = hash.get('token')

    if (fromUrl) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
      savePendingAdminInviteToken(fromUrl)
      setToken(fromUrl)
      return
    }

    const fromStorage = readPendingAdminInviteToken()
    if (fromStorage) {
      setToken(fromStorage)
    } else {
      setOutcome({ kind: 'invalid_link' })
    }
  }, [])

  useEffect(() => {
    if (!token || authLoading || !user || attempted.current) return
    attempted.current = true

    supabase.functions.invoke('accept-admin-invite', { body: { rawToken: token } }).then(({ data, error }) => {
      if (error) {
        clearPendingAdminInviteToken()
        setOutcome({ kind: 'error', message: ERROR_MESSAGES.internal_error })
        return
      }
      if (data?.ok) {
        clearPendingAdminInviteToken()
        setOutcome({ kind: 'success' })
        setTimeout(() => navigate('/app/admin', { replace: true }), 1500)
      } else {
        const code = data?.code ?? ''
        if (!RETRYABLE_BY_SWITCHING_ACCOUNT.has(code)) {
          clearPendingAdminInviteToken()
        } else {
          attempted.current = false
        }
        setOutcome({ kind: 'error', message: ERROR_MESSAGES[code] ?? ERROR_MESSAGES.internal_error, code })
      }
    })
  }, [token, authLoading, user, navigate])

  // No account yet: a dedicated, minimal sign-up — deliberately NOT Register.tsx's flow,
  // which always sets a primary_role in signup metadata (school_admin/instructor/
  // student). primary_role is permanently immutable once set (migration 015 denies
  // role → different for every caller, including service_role) — if this page routed an
  // invitee through Register.tsx first, they could never become drivetrack_admin
  // afterward. This signUp call sends no primary_role metadata at all, so
  // handle_new_user() leaves it NULL, matching exactly what accept-admin-invite's
  // `.is('primary_role', null)` guard requires.
  const handleCreateAccount = async (e: FormEvent) => {
    e.preventDefault()
    setSignupError('')
    if (!signupEmail.trim())    return setSignupError('Email is required.')
    if (signupPassword.length < 10) return setSignupError('Password must be at least 10 characters.')

    const { error } = await supabase.auth.signUp({
      email: signupEmail,
      password: signupPassword,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    })
    if (error) { setSignupError(mapSignUpError(error.message)); return }

    // Ride the same confirm-email handoff Register.tsx uses: AuthCallback.tsx's
    // isEmailConfirmation branch checks localStorage for this key, and (with the pending
    // admin-invite token already in sessionStorage from above) routes its "Email
    // confirmed" button to /admin/login instead of /login.
    localStorage.setItem('dt_signup_email', signupEmail.trim())
    setCheckEmailSent(true)
  }

  if (authLoading) return <LoadingScreen />

  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px', background: '#FAF6EF',
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={60} />
        </div>
        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 18, padding: '28px 24px', textAlign: 'center',
        }}>
          {children}
        </div>
      </div>
    </div>
  )

  if (outcome.kind === 'invalid_link') {
    return (
      <Shell>
        <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 10 }}>Invalid invitation link</div>
        <div style={{ font: '400 14px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)' }}>
          This link is missing its invitation code. Please use the link from your invitation email.
        </div>
      </Shell>
    )
  }

  if (!user) {
    if (checkEmailSent) {
      return (
        <Shell>
          <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 10 }}>Check your email</div>
          <div style={{ font: '400 14px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)' }}>
            We sent a confirmation link to {signupEmail}. Confirm it, then come back here to finish accepting your invitation.
          </div>
        </Shell>
      )
    }

    return (
      <Shell>
        <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 10 }}>
          You've been invited to administer DriveTrack
        </div>
        <div style={{ font: '400 14px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 22, textAlign: 'left' }}>
          Sign in with the account this invitation was sent to, or create a new account if you don't have one yet.
        </div>
        {!creatingAccount ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button className='btn-primary' onClick={() => navigate('/admin/login?redirect=/accept-admin-invite')}>
              Sign in
            </button>
            <button className='btn-teal' onClick={() => setCreatingAccount(true)}>
              Create an account
            </button>
          </div>
        ) : (
          <form onSubmit={handleCreateAccount} style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' }}>
            <FormField label='Email' type='email' value={signupEmail} onChange={e => setSignupEmail(e.target.value)} placeholder='you@example.com' autoComplete='email' required />
            <FormField label='Password' type='password' value={signupPassword} onChange={e => setSignupPassword(e.target.value)} placeholder='Min 10 characters' autoComplete='new-password' required />
            {signupError && <div className='banner-error'>{signupError}</div>}
            <button type='submit' className='btn-primary'>Create account</button>
          </form>
        )}
      </Shell>
    )
  }

  if (outcome.kind === 'success') {
    return (
      <Shell>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'rgba(15,124,108,0.12)', border: '2px solid #0F7C6C',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 18px',
        }}>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path d='M5 13l4 4L19 7' stroke='#0F7C6C' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'/>
          </svg>
        </div>
        <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 8 }}>You're in!</div>
        <div style={{ font: '400 14px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)' }}>
          Taking you to the admin portal…
        </div>
      </Shell>
    )
  }

  if (outcome.kind === 'error') {
    const canSwitchAccount = !!outcome.code && RETRYABLE_BY_SWITCHING_ACCOUNT.has(outcome.code)
    return (
      <Shell>
        <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 10 }}>Couldn't accept this invitation</div>
        <div className='banner-error' style={{ textAlign: 'left', marginBottom: 18 }}>{outcome.message}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {canSwitchAccount && (
            <button
              className='btn-primary'
              onClick={async () => {
                await supabase.auth.signOut()
                navigate('/admin/login?redirect=/accept-admin-invite')
              }}
            >
              Sign out and sign in with a different account
            </button>
          )}
          <button className={canSwitchAccount ? 'btn-teal' : 'btn-primary'} onClick={() => navigate('/')}>
            Continue to DriveTrack
          </button>
        </div>
      </Shell>
    )
  }

  return <LoadingScreen />
}
